import React from 'react';
import { LayoutDashboard } from 'lucide-react';

const DashboardMockup: React.FC = () => {
  return (
    <div className="bg-gray-50 rounded-2xl p-6 h-[380px] flex flex-col shadow-lg">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">Dashboard</h3>
        <div className="flex gap-2">
          <div className="flex flex-wrap gap-2 justify-end">
            <button className="px-2.5 py-1 rounded-full text-xs bg-white text-gray-800 border border-gray-300 shadow-sm">
              Marketing
            </button>
            <button className="px-2.5 py-1 rounded-full text-xs bg-white text-gray-800 border border-gray-300 shadow-sm">
              Website
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard columns */}
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 h-full overflow-auto">
          {/* Overdue column */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-red-100 rounded-lg">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-red-500">
                Overdue
                <span className="ml-2 text-xs font-medium bg-gray-200 text-gray-700 px-1.5 rounded-full">
                  2
                </span>
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {/* Task card 1 */}
              <div className="p-3 bg-white border-l-[4px] border-l-red-500 border border-gray-200 rounded-lg mb-2 shadow relative hover:shadow-md transition-shadow">
                <div className="flex items-center mb-1.5">
                  <span className="text-[10px] text-gray-500 font-medium">Website / Design</span>
                </div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1 pr-5">
                  API documentation
                </h3>
                <div className="text-red-500 text-xs mb-1 font-semibold">
                  <div className="flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Overdue by 2 days</span>
                  </div>
                </div>
              </div>

              {/* Task card 2 */}
              <div className="p-3 bg-white border-l-[4px] border-l-red-500 border border-gray-200 rounded-lg mb-2 shadow relative hover:shadow-md transition-shadow">
                <div className="flex items-center mb-1.5">
                  <span className="text-[10px] text-gray-500 font-medium">Marketing</span>
                </div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1 pr-5">Write blog post</h3>
                <p className="text-[10px] text-gray-600 mb-1.5 line-clamp-1">
                  Draft quarterly update
                </p>
                <div className="text-red-500 text-xs mb-1 font-semibold">
                  <div className="flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Overdue by 1 day</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Today column */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-green-500">
                Today
                <span className="ml-2 text-xs font-medium bg-gray-200 text-gray-700 px-1.5 rounded-full">
                  1
                </span>
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {/* Task card */}
              <div className="p-3 bg-white border-l-[4px] border-l-green-500 border border-gray-200 rounded-lg mb-2 shadow relative hover:shadow-md transition-shadow">
                <div className="flex items-center mb-1.5">
                  <span className="text-[10px] text-gray-500 font-medium">Marketing</span>
                </div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1 pr-5">
                  Social media meeting
                </h3>
                <p className="text-[10px] text-gray-600 mb-1.5 line-clamp-1">
                  Finalize Q2 campaign
                </p>
                <div className="text-green-500 text-xs mb-1 font-semibold">
                  <div className="flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Today 2:00 PM</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className="text-[10px] bg-white text-gray-600 px-1.5 py-0.5 rounded-full border border-gray-200">
                    meeting
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* This Week column */}
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-blue-500">
                This Week
                <span className="ml-2 text-xs font-medium bg-gray-200 text-gray-700 px-1.5 rounded-full">
                  2
                </span>
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto pr-1">
              {/* Task card 1 */}
              <div className="p-3 bg-white border-l-[4px] border-l-blue-500 border border-gray-200 rounded-lg mb-2 shadow relative hover:shadow-md transition-shadow">
                <div className="flex items-center mb-1.5">
                  <span className="text-[10px] text-gray-500 font-medium">Website</span>
                </div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1 pr-5">
                  User testing session
                </h3>
                <p className="text-[10px] text-gray-600 mb-1.5 line-clamp-1">
                  Test new onboarding flow
                </p>
                <div className="text-blue-500 text-xs mb-1.5 font-semibold">
                  <div className="flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Friday 10:00 AM</span>
                  </div>
                </div>
              </div>

              {/* Task card 2 */}
              <div className="p-3 bg-white border-l-[4px] border-l-blue-500 border border-gray-200 rounded-lg mb-2 shadow relative hover:shadow-md transition-shadow">
                <div className="flex items-center mb-1.5">
                  <span className="text-[10px] text-gray-500 font-medium">Website</span>
                </div>
                <h3 className="text-gray-900 font-semibold text-sm mb-1 pr-5">
                  Review analytics
                </h3>
                <div className="text-blue-500 text-xs mb-1.5 font-semibold">
                  <div className="flex items-center gap-1">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>Thursday 2:00 PM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const dashboardMockupData = {
  type: 'Dashboard',
  icon: LayoutDashboard,
  description:
    "Get an overview of all your tasks organized by status and deadline. See what's overdue, due today, coming up this week, and your highlighted important tasks - all in one place. The perfect command center for managing your priorities.",
  mockup: <DashboardMockup />
};

export default DashboardMockup;